import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
export type ContactsCallingFilterPanelMode = "contacts" | "calling";

export interface ContactsCallingFilterPanelProps {
  mode: ContactsCallingFilterPanelMode;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (v: string) => void;
  filterName: string;
  filterEmail: string;
  filterCreatedAfter: string;
  filterCreatedBefore: string;
  onFilterNameChange: (v: string) => void;
  onFilterEmailChange: (v: string) => void;
  onFilterCreatedAfterChange: (v: string) => void;
  onFilterCreatedBeforeChange: (v: string) => void;
  /** calling only */
  filterClosingAfter?: string;
  filterClosingBefore?: string;
  onFilterClosingAfterChange?: (v: string) => void;
  onFilterClosingBeforeChange?: (v: string) => void;
  /** Sheet footer: clear + apply close */
  showApplyRow?: boolean;
  onClear?: () => void;
  onApply?: () => void;
  /** Volanie: subpipeline + fázis választók közvetlenül a kereső alatt */
  belowSearch?: ReactNode;
}

export function ContactsCallingFilterPanel({
  mode,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  filterName,
  filterEmail,
  filterCreatedAfter,
  filterCreatedBefore,
  onFilterNameChange,
  onFilterEmailChange,
  onFilterCreatedAfterChange,
  onFilterCreatedBeforeChange,
  filterClosingAfter = "",
  filterClosingBefore = "",
  onFilterClosingAfterChange,
  onFilterClosingBeforeChange,
  showApplyRow = false,
  onClear,
  onApply,
  belowSearch,
}: ContactsCallingFilterPanelProps) {
  const { t } = useTranslation();
  const isCalling = mode === "calling";

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {belowSearch ? <div className="space-y-3">{belowSearch}</div> : null}

      <div>
        <label className="mb-1 block text-sm font-medium">{t("calling.filterCreatedAt")}</label>
        <div className="flex flex-wrap gap-2">
          <Input
            type="date"
            value={filterCreatedAfter}
            onChange={(e) => onFilterCreatedAfterChange(e.target.value)}
            className="min-w-[120px] flex-1"
          />
          <Input
            type="date"
            value={filterCreatedBefore}
            onChange={(e) => onFilterCreatedBeforeChange(e.target.value)}
            className="min-w-[120px] flex-1"
          />
        </div>
      </div>

      {isCalling && (
        <div>
          <label className="mb-1 block text-sm font-medium">{t("calling.filterClosingDate")}</label>
          <div className="flex flex-wrap gap-2">
            <Input
              type="date"
              value={filterClosingAfter}
              onChange={(e) => onFilterClosingAfterChange?.(e.target.value)}
              className="min-w-[120px] flex-1"
            />
            <Input
              type="date"
              value={filterClosingBefore}
              onChange={(e) => onFilterClosingBeforeChange?.(e.target.value)}
              className="min-w-[120px] flex-1"
            />
          </div>
        </div>
      )}

      <div>
        <label className="mb-1 block text-sm font-medium">{t("calling.filterName")}</label>
        <Input value={filterName} onChange={(e) => onFilterNameChange(e.target.value)} placeholder={t("calling.filterName")} />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">{t("calling.filterEmail")}</label>
        <Input
          type="email"
          value={filterEmail}
          onChange={(e) => onFilterEmailChange(e.target.value)}
          placeholder={t("calling.filterEmail")}
        />
      </div>

      {showApplyRow && onClear && (
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" type="button" onClick={onClear}>
            {t("calling.filterClear")}
          </Button>
          {onApply && (
            <Button className="flex-1" type="button" onClick={onApply}>
              {t("calling.filterApply")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
